import _ from "lodash";
import { format, parse } from "date-fns";
import Entity from "entity";
import ImageDB from "entity/ImageDB";
import getAllTiposIndicador, { getTiposIndicadorByField } from "entity/TipoIndicador/all";
import Fazenda from "entity/Fazenda";
import Cliente from "entity/Cliente";
import User from "entity/User";
import { clearAtributes } from "util/object";
import { getIndicadorByField } from "entity/Indicador/all";

const dateFormat = "dd/MM/yyyy";
const strDateFormat = "yyyyMMdd";

export default class LaudoVisita extends Entity {
    static get collectionUID() {
        return "ValueCustomForm/sSCy71ONYFEwlGVDK4kK/Values";
    }

    async initialize() {
        this.data.status = "Em Digitação";
        this.data.data = new Date();
        this.data.tecnico = (await User.getCurrent()).data;
    }

    async onLoad() {
        if (this.data.data) {
            this.data.data = parse(this.data.data, dateFormat, new Date());
        }

        // TODO: armazenar promises e mover awaits para o final, para permitir carregar em paralelo.
        await this.loadFazenda();

        await ImageDB.loadBase64Array(this.data.itensImagemPtecnico);

        for (const TipoClass of getAllTiposIndicador()) {
            await TipoClass.onLoad(this);
        }
    }

    async preSave(entity) {
        entity.data.versionData = 1;

        const tiposIndicadores = getAllTiposIndicador();
        const apontamentos = entity.data.apontamentos || []

        Object.keys(apontamentos).forEach((tipoKey) => {
            const tipoIndicador = tiposIndicadores.find(({ field }) => field === tipoKey);

            if (tipoIndicador) {
                // get the fields of each indicator inside the type
                const indicadoresValidos = tipoIndicador.indicadores.map((indicador) => indicador.field);

                Object.keys(apontamentos[tipoKey]).forEach((indicadorKey) => {
                    if (indicadoresValidos.includes(indicadorKey)) {
                        const coletas = apontamentos[tipoKey][indicadorKey];

                        if (Array.isArray(coletas)) {
                            coletas.forEach((coleta, index) => {
                                if (typeof coleta === "object" && coleta !== null) {
                                    clearAtributes(coleta, "equipamento.cliente", ["uid", "nome"]);
                                    clearAtributes(coleta, "equipamento", ["uid", "equipamento"]);
                                    clearAtributes(coleta, "frente.cliente", ["uid", "nome"]);
                                    clearAtributes(coleta, "frente", ["uid", "frente", "servico"]);
                                    clearAtributes(coleta, "trator.cliente", ["uid", "nome"]);
                                    clearAtributes(coleta, "trator", ["uid", "marcaTrator", "modeloTrator", "numeroFrotaTrator", ""]);
                                }
                            });
                        }
                    }
                });
            }
        });

        //Limpa Dados do Save cliente da fazenda
        clearAtributes(entity.data, "fazenda.cliente", ["uid", "nome"]);
        clearAtributes(entity.data, "cultura", ["uid", "cultura"]);
        clearAtributes(entity.data, "safra", ["uid", "safra"]);
        clearAtributes(entity.data, "tecnico", ["uid", "nome", "email", "grupoPermissao"]);

        entity.data.strIndicadores = LaudoVisita.generateStrIndicador(entity.data);

        const date = entity.data.data;

        if (date) {
            entity.data.data = format(date, dateFormat);
            entity.data.dataStr = format(date, strDateFormat);
        }

        // Referência ao objeto que criou a imagem.
        const ref = { type: "LaudoVisita", uid: entity.data.uid };
        await ImageDB.fromBase64Array(entity.data.itensImagemPtecnico, ref);

        for (const TipoClass of getAllTiposIndicador()) {
            await TipoClass.preSave(entity);
        }
    }

    static async delete(uid) {
        // super.delete
        // TODO: remover imagens
    }

    /**
     * Uma lista de todas as frentes nos apontamentos.
     */
    get frentes() {
        if (!this._frentes) {
            const result = new Set();

            for (const TipoClass of getAllTiposIndicador()) {
                const apontamentos = this.getApontamentosByTipo(TipoClass);

                for (const indicador in apontamentos) {
                    for (const apontamento of apontamentos[indicador]) {
                        if (apontamento.frente) {
                            result.add(apontamento.frente);
                        }
                    }
                }
            }

            this._frentes = [...result];
        }

        return this._frentes;
    }

    /**
     * Marca a imagem para ser excluída do banco de dados quando a visita for salva.
     */
    deleteImage(imageUid) {
        this.deleteRelated(ImageDB, imageUid);
    }

    /**
     * Retorna os apontamentos do tipo especificado.
     */
    getApontamentosByTipo(TipoClass) {
        return _.get(this.data, `apontamentos.${TipoClass.field}`) || [];
    }

    /**
     * Retorna os apontamentos do indicador especificado.
     *
     * @param {*} indicador
     * @param {*} tipoName (Opcional) "Manual" ou "Mecanizado".
     * @returns
     */
    getApontamentosByIndicador(indicador, tipoName) {
        let apontamentos =
            _.get(this.data, `apontamentos.${indicador.type.field}.${indicador.constructor.field}`) || [];

        if (tipoName) {
            apontamentos = _.filter(apontamentos, (a) => a.tipo === tipoName);
        }
        return apontamentos;
    }

    /**
     * Carrega fazenda e cliente.
     *
     * Se os dados da fazenda não forem fornecidos, carrega do banco de dados a partir do
     * campo 'fazenda.uid' da visita.
     */
    async loadFazenda(fazendaData) {
        if (fazendaData) {
            this.fazenda = new Fazenda();
            await this.fazenda.loadData(fazendaData);
        } else {
            const fazendaUid = _.get(this.data, "fazenda.uid");
            if (fazendaUid) {
                this.fazenda = await Fazenda.get(fazendaUid);
            }
        }

        this.data.fazenda = this.fazenda.data;

        // TODO: armazenar apenas uid e talvez nome?
        // this.data.fazenda = {
        //     uid: fazendaUid,
        //     nomeFazenda: this.fazenda.data.nomeFazenda,
        // };

        const clienteUid = _.get(this.data.fazenda, "cliente.uid");
        this.cliente = await Cliente.get(clienteUid);
        this.cliente.parseNewParams();
        if (this.cliente) {
            if (!this.cliente.data.params) {
                this.data.cliente = null;
                this.data.fazenda = null;
                this.notify();
                throw new Error("Cliente não possui nenhum parametro");
            }
            this.data.cliente = this.cliente.data;
        } else {
            this.data.cliente = this.fazenda.data.cliente;
        }
    }

    static generateStrIndicador(laudoData) {
        let str = [];

        for (const TipoClass of getAllTiposIndicador()) {
            let ap = _.get(laudoData, `apontamentos.${TipoClass.field}`);

            for (const key in ap) {
                let strInd = getIndicadorByField(key).abbrev;
                if (!str.includes(strInd)) {
                    str.push(strInd);
                }
            }
        }
        return str.join(", ");
    }

    static get csvColumns() {
        return [
            { labelCol: "Nº da Visita", getValue: "sequencialIndex" },
            { labelCol: "Data", getValue: "data" },
            { labelCol: "Cliente", getValue: "cliente.nome" },
            { labelCol: "Fazenda", getValue: "fazenda.nomeFazenda" },
            { labelCol: "Técnico", getValue: "tecnico.nome" },
            {
                labelCol: "Número de coletas",
                getValue: (v) => {
                    // console.log(v.sequencialIndex)
                    // console.log(v.cliente.nome)
                    // console.log(v.data)
                    let rowCell = "";

                    if (v.apontamentos) {
                        let tipoIndicadorArrStr = Object.keys(v.apontamentos);

                        // percorrendo os tipos de indicador
                        for (let i = 0; i < tipoIndicadorArrStr.length; i++) {
                            rowCell = rowCell + getTiposIndicadorByField(tipoIndicadorArrStr[i]).abbrev + " (";

                            let tiposInd = _.get(v.apontamentos, tipoIndicadorArrStr[i]);
                            let indArrStr = Object.keys(tiposInd);

                            // percorrendo os indicadores
                            for (let j = 0; j < indArrStr.length; j++) {
                                let indClass = getIndicadorByField(indArrStr[j]);
                                let pontosInd = _.get(tiposInd, indArrStr[j]);
                                // let pontosLength = pontosInd.length
                                let coletasLength = 0;

                                // console.log(`${indClass.abbrev}: ${pontosLength}`)

                                // percorrendo os pontos de coletas dos indicadores
                                for (let k = 0; k < pontosInd.length; k++) {
                                    if (pontosInd[k].coletas) {
                                        coletasLength += pontosInd[k].coletas.length;
                                    } else {
                                        coletasLength += 1;
                                    }
                                }

                                rowCell = rowCell + indClass.abbrev + ": " + coletasLength + ", ";
                            }
                            rowCell = rowCell.substring(0, rowCell.length - 2); // remove ', ' do ultimo indicador

                            rowCell = rowCell + "), ";
                        }
                        rowCell = rowCell.substring(0, rowCell.length - 2); // remove ', ' do ultimo tipo de indicador

                        // console.log(rowCell)

                        // console.log('-----------')
                        return rowCell;
                    }
                    // console.log('-----------')
                    return "0";
                },
            },
            {
                labelCol: "Total de coletas",
                getValue: (v) => {
                    let total = 0;

                    if (v.apontamentos) {
                        let tipoIndicadorArrStr = Object.keys(v.apontamentos);

                        // percorrendo os tipos de indicador da visita
                        for (let i = 0; i < tipoIndicadorArrStr.length; i++) {
                            let tiposIndicadorClass = _.get(v.apontamentos, tipoIndicadorArrStr[i]);
                            let indArrStr = Object.keys(tiposIndicadorClass);

                            // percorrendo os indicadores da visita
                            for (let j = 0; j < indArrStr.length; j++) {
                                let pontosInd = _.get(tiposIndicadorClass, indArrStr[j]);

                                // percorrendo os pontos de coletas dos indicadores
                                for (let k = 0; k < pontosInd.length; k++) {
                                    if (pontosInd[k].coletas) {
                                        total += pontosInd[k].coletas.length;
                                    } else {
                                        total++;
                                    }
                                }
                            }
                        }
                        return total;
                    }
                    return "0";
                },
            },
            { labelCol: "UID Cliente", getValue: "cliente.uid" },
            { labelCol: "UID Fazenda", getValue: "fazenda.uid" },
            { labelCol: "UID Técnico", getValue: "tecnico.uid" },
        ];
    }
}
